import { Box, Grid } from '@chakra-ui/react'
import { Lato16Bold } from '@sequencehq/design-tokens'
import { useBillingScheduleAggregate } from 'modules/BillingScheduleHome/utils/useBillingScheduleAggregate'
import { useBillingScheduleId } from 'modules/BillingScheduleHome/utils/useBillingScheduleId'
import { ScheduleDetailItems } from './ScheduleDetailItems'
import { parse } from '@sequencehq/utils/dates'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { SectionError } from '../../common/SectionError'
import { Skeleton } from '@sequencehq/core-components'
import { API_DATE_FORMAT } from 'BillingScheduleHome/billingScheduleHome.constants'
import fill from 'lodash/fill'

const useScheduleDetailsSection = () => {
  const billingScheduleId = useBillingScheduleId()
  const billingScheduleAggregateQuery = useBillingScheduleAggregate({
    billingScheduleId,
    select: data => {
      return {
        customerId: data.billingSchedule.customerId,
        customerContacts: data.customerContacts.map(contact => ({
          id: contact.id,
          name: contact.name,
          email: contact.email
        })),
        startDate: parse(
          data.billingSchedule.startDate,
          API_DATE_FORMAT,
          new Date()
        ),
        endDate: data.billingSchedule.endDate
          ? parse(data.billingSchedule.endDate, API_DATE_FORMAT, new Date())
          : undefined,
        paymentMethod: data.billingScheduleSettings?.paymentProvider,
        autoChargeCustomer: data.billingScheduleSettings?.autoCharge,
        autoIssueInvoices: data.billingSchedule.autoIssueInvoices,
        purchaseOrderNumber: data.billingSchedule.purchaseOrderNumber,
        label: data.billingSchedule.label
      }
    }
  })

  return {
    isLoading: billingScheduleAggregateQuery.isPending,
    isError: billingScheduleAggregateQuery.isError,
    ...billingScheduleAggregateQuery.data
  }
}

export const ScheduleDetailsSection = () => {
  const hook = useScheduleDetailsSection()
  const flags = useFlags()

  if (hook.isError) {
    return (
      <Box>
        <Box {...Lato16Bold} marginBottom="16px">
          Schedule details
        </Box>
        <Box padding="16px" overflow="hidden">
          <SectionError
            size="medium"
            text="An error occurred when loading the schedule details"
          />
        </Box>
      </Box>
    )
  }

  if (hook.isLoading) {
    return (
      <Box>
        <Box {...Lato16Bold} marginBottom="16px">
          Schedule details
        </Box>
        <Grid templateColumns="1fr 1fr" gap="128px">
          <Grid
            templateColumns="158px 1fr"
            gridAutoFlow="row"
            templateRows="32px 32px 32px"
            gridColumnGap="16px"
            gridRowGap="8px"
          >
            {fill(Array(6), null).map((_, idx) => (
              <Skeleton height="32px" width="100%" key={idx} />
            ))}
          </Grid>
          <Grid
            templateColumns="158px 1fr"
            templateRows="32px 32px 32px 32px"
            gridAutoFlow="row"
            gridColumnGap="16px"
            gridRowGap="8px"
            height="fit-content"
          >
            {fill(Array(8), null).map((_, idx) => (
              <Skeleton height="32px" width="100%" key={idx} />
            ))}
          </Grid>
        </Grid>
      </Box>
    )
  }

  return (
    <Box padding="8px 0">
      <Box {...Lato16Bold} marginBottom="16px">
        Schedule details
      </Box>
      <Grid templateColumns="1fr 1fr" gridColumnGap="16px">
        <Box>
          <ScheduleDetailItems.Customer customerId={hook.customerId} />
          {Boolean(hook.customerContacts?.length) && (
            <ScheduleDetailItems.BillingContacts
              contacts={hook.customerContacts}
            />
          )}
          <ScheduleDetailItems.StartDate startDate={hook.startDate} />
          <ScheduleDetailItems.EndDate endDate={hook.endDate} />
        </Box>
        <Box>
          {hook.paymentMethod === 'STRIPE' && (
            <>
              <ScheduleDetailItems.PaymentMethod paymentMethod="STRIPE" />
              <ScheduleDetailItems.AutoChargeCustomer
                autoChargeCustomer={hook.autoChargeCustomer}
              />
            </>
          )}
          {flags.enableAutoIssueInvoices && (
            <ScheduleDetailItems.AutoIssueInvoices
              autoIssueInvoices={hook.autoIssueInvoices}
            />
          )}
          <ScheduleDetailItems.PurchaseOrderNumber
            purchaseOrderNumber={hook.purchaseOrderNumber}
          />
          {flags.showBillingScheduleLabelField && (
            <ScheduleDetailItems.Label label={hook.label} />
          )}
        </Box>
        <Box></Box>
      </Grid>
    </Box>
  )
}
